import { useCallback, useEffect, useReducer } from 'react';

import { SortOrder } from '@ariksa/notification';
import { FilterEntityType } from '@ariksa/notification/api';
import { find, map, toUpper } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { cloudProviderOptionsWithAll, getIcon } from 'components/DataDisplay';
import { severityOptionsWithAll } from 'components/DataDisplay/Utils/renderSeverity';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { FindingsIssuesSearchParamProps } from 'containers/Findings/Alerts/types';
import {
  overDueFilterOptions,
  ticketStatusOptions,
} from 'containers/Findings/Tickets/Components/utils';
import { selectTickets } from 'containers/Findings/Tickets/selectors';
import { actions } from 'containers/Findings/Tickets/slice';
import { selectSharedState } from 'containers/SharedState/selectors';
import { useSearchParams } from 'hooks/useSearchParams';

export const useTickets = () => {
  const dispatch = useDispatch();
  const { assignees, tickets } = useSelector(selectTickets);
  const { alertResourceTypes, finalSearchTerm } = useSelector(
    selectSharedState,
  );
  const { accountId, environmentId } = useAccessBoundary();
  const params = useSearchParams<FindingsIssuesSearchParamProps>();
  const { updateValueInURL, updateValuesInURL } = useSearchParamFunctions();
  const {
    getResourceAlias,
    getAlertResourceTypeOptionsWithAll,
  } = useResourceType();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      severity: severityOptionsWithAll[0],
      status: { label: 'State', value: undefined },
      resourceType: {},
      provider: cloudProviderOptionsWithAll[0],
      resourceTypeOptions: [],
      assigneeOptions: [],
      assignee: { label: 'Owner', value: undefined },
      isOverDueDate: false,
      sortOrder: SortOrder.Asc,
      sortField: '',
    },
  );

  useEffect(() => {
    dispatch(
      actions.getAssignees({
        q: {
          environmentId: environmentId!,
          accountIds: accountId ? [accountId] : undefined,
          filterEntity:
            params?.viewBy !== 'findings'
              ? FilterEntityType.FindingsTicket
              : FilterEntityType.IssueTicket,
        },
      }),
    );
  }, [dispatch, environmentId, accountId, params?.viewBy]);

  //set ticket status
  useEffect(() => {
    const status = params?.status;
    updateState({
      status:
        find(ticketStatusOptions, o => o.value === status) ??
        ticketStatusOptions[0],
    });
  }, [params.status]);

  //set resource type options
  useEffect(() => {
    const options = getAlertResourceTypeOptionsWithAll(alertResourceTypes.data);
    updateState({
      resourceTypeOptions: options,
    });
  }, [alertResourceTypes.data, getAlertResourceTypeOptionsWithAll]);

  //set resource type
  useEffect(() => {
    const resourceType = !!params?.resourceType
      ? {
          label: getResourceAlias(params?.resourceType),
          value: params?.resourceType,
          icon: getIcon(params?.resourceType),
        }
      : state.resourceTypeOptions?.[0];
    updateState({
      resourceType,
    });
  }, [
    dispatch,
    params.resourceType,
    getResourceAlias,
    state.resourceTypeOptions,
  ]);

  //set severity
  useEffect(() => {
    const severity = params?.severity?.toLowerCase();
    updateState({
      severity:
        find(severityOptionsWithAll, o => o.value === severity) ??
        severityOptionsWithAll[0],
    });
  }, [dispatch, params.severity]);

  //set provider
  useEffect(() => {
    const provider = params?.provider;
    updateState({
      provider:
        find(cloudProviderOptionsWithAll, o => o.value === provider) ??
        cloudProviderOptionsWithAll[0],
    });
  }, [dispatch, params.provider]);

  //set is over sla
  useEffect(() => {
    const isOverDueDate = params?.isOverDueDate === 'true';
    updateState({
      isOverDueDate: isOverDueDate
        ? overDueFilterOptions[0]
        : overDueFilterOptions[1],
    });
  }, [dispatch, params.isOverDueDate]);

  //set assignee options
  useEffect(() => {
    const options = [
      { label: 'Owner', value: undefined },
      ...map(assignees.data, o => ({
        label: o.assignee_name,
        value: o.assignee_id,
      })),
    ];
    updateState({ assigneeOptions: options });
  }, [assignees.data]);

  useEffect(() => {
    const assignee = params?.assignee;
    updateState({
      assignee:
        find(state.assigneeOptions, o => o.value === assignee) ??
        state.assigneeOptions?.[0],
    });
  }, [state.assigneeOptions, params?.assignee]);

  //push value to local storage and set url params
  const updateValue = useCallback(
    (field, value) => {
      updateValueInURL(
        field,
        !!value
          ? field === 'severity'
            ? value?.value?.toUpperCase()
            : value?.value
          : '',
      );
    },
    [updateValueInURL],
  );

  // Both sort params go in one URL update; two separate updates would each
  // start from the same old URL and the second would drop the first.
  const updateSort = useCallback(
    ({ sortField, sortOrder }: { sortField?: string; sortOrder?: string }) => {
      updateValuesInURL({ sortField, sortOrder });
    },
    [updateValuesInURL],
  );

  const getTickets = useCallback(() => {
    if (!!params?.sortOrder && !!params?.sortField)
      updateState({
        sortOrder: params?.sortOrder as SortOrder,
        sortField: params?.sortField,
      });
  }, [params?.sortOrder, params?.sortField]);

  useEffect(() => {
    const payload = {
      environmentId: environmentId as string,
      accountIds: !!accountId ? [accountId] : undefined,
      state: state?.status?.value,
      severity: state?.severity?.value,
      sortField: !!state?.sortField ? state?.sortField : undefined,
      sortOrder: !!state?.sortOrder
        ? (toUpper(state?.sortOrder) as SortOrder)
        : undefined,
      search: finalSearchTerm,
      pageNumber: tickets.page.info.page_number,
      pageSize: tickets.page.info.page_size,
      assigneeId: state?.assignee?.value,
    };
    if (params?.viewBy === 'findings') {
      dispatch(
        actions.getGroupTickets({
          q: payload,
        }),
      );
    } else
      dispatch(
        actions.getTickets({
          q: {
            ...payload,
            resourceType: state?.resourceType?.value,
            overDueDate: state?.isOverDueDate,
            cloud: state?.provider?.value,
          },
        }),
      );
  }, [
    environmentId,
    accountId,
    dispatch,
    state.severity,
    state.isOverDueDate,
    state.resourceType,
    state.sortOrder,
    state.sortField,
    finalSearchTerm,
    tickets.page.info,
    state.assignee,
    state?.provider,
    state.status,
    params.viewBy,
  ]);

  return {
    getTickets,
    updateValue,
    updateSort,
    severity: state.severity,
    status: state.status,
    assignee: state.assignee,
    provider: state.provider,
    resourceTypeOptions: state.resourceTypeOptions,
    resourceType: state.resourceType,
    isOverDueDate: state.isOverDueDate,
    sortOrder: state.sortOrder,
    sortField: state.sortField,
    assigneeOptions: state.assigneeOptions,
  };
};
